// Links the shapes of a Mapping's sector map (SVG) to its Sectors and Sections.
//
// Linking rewrites a shape's id to `_<sector-uuid>` or `_<section-uuid>`, which is what the
// SPA's SvgMap reads. There is no mapping table: the rewritten SVG is the Mapping's image.
import { computed, ref, shallowRef, type ComputedRef, type Ref, type ShallowRef } from 'vue'

export interface LinkableSection {
  id: string
  name: string
}

export interface LinkableSector {
  id: string
  name: string
  sections: LinkableSection[]
}

export interface Target {
  kind: 'sector' | 'section'
  id: string
  name: string
  /** "Sector › Section" for Sections */
  label: string
  sectorId: string
  color: string
}

export interface Shape {
  /** Stable for the life of the loaded file, unlike the id, which linking rewrites */
  key: string
  tag: string
  depth: number
  parentKey: string | null
  childCount: number
  id: string | null
  /** Null when the shape is unlinked or its link has a problem */
  target: Target | null
  /** An error with the shape's id, which blocks saving */
  problem: string | null
  /** Too small to click (the designer's outlined letters): left out of picking and hidden by default */
  small: boolean
}

export type BoxOf = (element: Element) => { width: number, height: number }

export interface Issue {
  level: 'error' | 'warning' | 'info'
  text: string
  /** The shape the issue is about, when there is one */
  key?: string
}

const SVG_NS = 'http://www.w3.org/2000/svg'
const SHAPES = 'g, path, polygon, polyline, rect, circle, ellipse'
/** Same list as ticket-api's SvgSanitizer::OUTSIDE_ROOT, which wraps uploads the same way */
const OUTSIDE_ROOT = ['defs', 'metadata', 'style', 'title', 'desc']
/** The tool's own attributes, which are never saved */
const OWN = 'data-link-'
const KEY = `${OWN}key`
/** "small" or "background": shapes that don't take clicks */
const PICK = `${OWN}pick`
/** Of the drawing's area */
const SMALL = 0.003
const BACKGROUND = 0.9
const LINKED_ID = /^_([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i

/** One per Sector, in order, for the name chips on the map */
const COLORS = ['#1e88e5', '#43a047', '#fb8c00', '#8e24aa', '#e53935', '#00acc1', '#6d4c41', '#3949ab']

function uuidOf(id: string | null): string | null {
  return LINKED_ID.exec(id ?? '')?.[1].toLowerCase() ?? null
}

function linkedUuid(e: Element): string | null {
  return uuidOf(e.id)
}

/** The background is left out of the tree, unless its id needs fixing */
function isListed(e: Element): boolean {
  return e.hasAttribute(KEY) && (e.getAttribute(PICK) !== 'background' || linkedUuid(e) !== null)
}

/** How the history refers to a shape */
function nameOf(e: Element): string {
  const key = e.getAttribute(KEY)
  return key ? `${e.localName} ${key}` : e.localName
}

/** Client-side safety net: the API sanitises the file again when it's saved. */
function strip(svg: SVGSVGElement): void {
  svg.querySelectorAll('script, foreignObject').forEach(e => e.remove())
  svg.querySelectorAll('*').forEach((e) => {
    for (const attribute of [ ...e.attributes ]) {
      if (attribute.name.toLowerCase().startsWith('on')) e.removeAttribute(attribute.name)
    }
  })
}

function parse(markup: string): SVGSVGElement {
  const parsed = new DOMParser().parseFromString(markup, 'image/svg+xml')
  const root = parsed.documentElement

  if (parsed.querySelector('parsererror') || root.namespaceURI !== SVG_NS || root.localName !== 'svg') {
    throw new Error('O arquivo não é um SVG válido.')
  }

  return document.importNode(root, true) as unknown as SVGSVGElement
}

export function useSectorMapLinking(source: { mappingId: () => string, sectors: () => LinkableSector[] }) {
  const svg: ShallowRef<SVGSVGElement | null> = shallowRef(null)
  /** Bumped on every change to the SVG, which Vue can't observe */
  const version: Ref<number> = ref(0)
  const createdRoot: Ref<boolean> = ref(false)
  /** Whether the SVG differs from the file that was loaded */
  const changed: Ref<boolean> = ref(false)
  /** Oldest first */
  const history: Ref<string[]> = ref([])
  const smallKeys: Ref<Set<string>> = ref(new Set())
  let nextKey = 0

  const targets: ComputedRef<Target[]> = computed<Target[]>(() => source.sectors().flatMap((sector, index) => {
    const color = COLORS[index % COLORS.length]

    return [
      { kind: 'sector' as const, id: sector.id, name: sector.name, label: sector.name, sectorId: sector.id, color },
      ...sector.sections.map(section => ({
        kind: 'section' as const,
        id: section.id,
        name: section.name,
        label: `${sector.name} › ${section.name}`,
        sectorId: sector.id,
        color,
      })),
    ]
  }))

  const targetById: ComputedRef<Map<string, Target>> = computed<Map<string, Target>>(() => new Map(targets.value.map(t => [t.id, t])))

  function load(markup: string): void {
    const element = parse(markup)
    strip(element)

    history.value = []
    changed.value = false
    createdRoot.value = element.querySelector('g#root') === null

    if (createdRoot.value) {
      wrapInRoot(element)
      history.value.push('Grupo #root criado em volta do desenho.')
    }

    nextKey = 0
    element.querySelector('g#root')!.querySelectorAll(SHAPES).forEach((e) => {
      e.setAttribute(KEY, `s${nextKey++}`)

      const uuid = linkedUuid(e)
      if (uuid && e.id !== `_${uuid}`) {
        history.value.push(`${nameOf(e)}: ${e.id} passou para minúsculas.`)
        e.id = `_${uuid}`
        changed.value = true
      }
    })

    smallKeys.value = new Set()
    svg.value = element
    version.value++
  }

  function clear(): void {
    svg.value = null
    history.value = []
    changed.value = false
    createdRoot.value = false
    smallKeys.value = new Set()
  }

  /**
   * Called once the SVG is in the document, where it can be measured. Only leaf shapes are left out
   * of picking, never a group, because `pointer-events` is inherited.
   */
  function measure(boxOf: BoxOf = e => (e as SVGGraphicsElement).getBBox()): void {
    const root = svg.value?.querySelector('g#root')
    if (!root) return

    const rootBox = boxOf(root)
    const total = Math.max(rootBox.width * rootBox.height, 1)
    const small = new Set<string>()

    root.querySelectorAll(`[${KEY}]`).forEach((e) => {
      e.removeAttribute(PICK)
      if (e.localName === 'g') return

      const box = boxOf(e)
      const area = (box.width * box.height) / total

      if (area > BACKGROUND) {
        e.setAttribute(PICK, 'background')
      } else if (area < SMALL) {
        e.setAttribute(PICK, 'small')
        small.add(keyOf(e)!)
      }
    })

    smallKeys.value = small
    version.value++
  }

  function wrapInRoot(element: SVGSVGElement): void {
    const root = document.createElementNS(SVG_NS, 'g')
    root.id = 'root'

    for (const child of [ ...element.childNodes ]) {
      if (child instanceof Element && OUTSIDE_ROOT.includes(child.localName)) continue
      root.appendChild(child)
    }

    element.appendChild(root)
  }

  function keyOf(e: Element | null | undefined): string | null {
    return e?.getAttribute(KEY) ?? null
  }

  function ancestors(e: Element): Element[] {
    const list: Element[] = []
    let current = e.parentElement?.closest(`[${KEY}]`)

    while (current) {
      list.push(current)
      current = current.parentElement?.closest(`[${KEY}]`)
    }

    return list
  }

  function problemOf(e: Element, target: Target | null, uses: Map<string, number>): string | null {
    const uuid = linkedUuid(e)
    if (!uuid) return null

    if (!target) {
      return uuid === source.mappingId()
        ? 'tem o id do Mapeamento, não de um Setor ou Seção'
        : 'aponta para um Setor ou Seção que não existe mais neste Mapeamento'
    }

    if ((uses.get(uuid) ?? 0) > 1) return `${target.label} está em mais de uma forma`

    if (target.kind === 'section') {
      const otherSector = ancestors(e)
        .map(ancestor => targetById.value.get(linkedUuid(ancestor) ?? ''))
        .find(ancestor => ancestor?.kind === 'sector' && ancestor.id !== target.sectorId)

      if (otherSector) return `está dentro do grupo de ${otherSector.name}`
    }

    return null
  }

  const shapes: ComputedRef<Shape[]> = computed<Shape[]>(() => {
    void version.value
    if (!svg.value) return []

    const elements = [ ...svg.value.querySelectorAll(`[${KEY}]`) ].filter(isListed)
    const uses = new Map<string, number>()

    for (const e of elements) {
      const uuid = linkedUuid(e)
      if (uuid) uses.set(uuid, (uses.get(uuid) ?? 0) + 1)
    }

    return elements.map((e) => {
      const target = targetById.value.get(linkedUuid(e) ?? '') ?? null
      const problem = problemOf(e, target, uses)

      return {
        key: keyOf(e)!,
        tag: e.localName,
        depth: ancestors(e).length,
        parentKey: keyOf(e.parentElement?.closest(`[${KEY}]`)),
        childCount: [ ...e.children ].filter(isListed).length,
        id: e.id || null,
        target: problem ? null : target,
        problem,
        small: smallKeys.value.has(keyOf(e)!),
      }
    })
  })

  const issues: ComputedRef<Issue[]> = computed<Issue[]>(() => {
    if (!svg.value) return []

    const list: Issue[] = []

    for (const shape of shapes.value) {
      if (shape.problem) list.push({ level: 'error', text: `${shape.tag} ${shape.id}: ${shape.problem}`, key: shape.key })
    }

    // A target on a shape with a problem is reported by that error, not as missing too
    const linked = new Set(shapes.value.flatMap(shape => uuidOf(shape.id) ?? []))

    // The buyer's click must say which Section to buy, so a Sector shape only stands in for its
    // Section when it has exactly one: a multi-Section Sector is reported through its Sections.
    for (const sector of source.sectors()) {
      const covered = linked.has(sector.id) || sector.sections.some(section => linked.has(section.id))
      const missing = sector.sections.length > 1
        ? sector.sections.filter(section => !linked.has(section.id))
        : covered ? [] : [ sector ]

      for (const target of missing) {
        list.push({ level: 'warning', text: `${targetById.value.get(target.id)!.label} não tem forma no mapa` })
      }
    }

    if (createdRoot.value) list.push({ level: 'info', text: 'O arquivo não tinha #root: o grupo foi criado.' })

    return list
  })

  const smallCount: ComputedRef<number> = computed<number>(() => shapes.value.filter(s => s.small).length)

  const errorCount: ComputedRef<number> = computed<number>(() => issues.value.filter(i => i.level === 'error').length)

  /** The key of the shape an event happened on, for the map */
  function keyAt(target: EventTarget | null): string | null {
    return target instanceof Element ? keyOf(target.closest(`[${KEY}]`)) : null
  }

  function element(key: string): Element | null {
    return svg.value?.querySelector(`[${KEY}="${key}"]`) ?? null
  }

  function recordChange(entry: string): void {
    history.value.push(entry)
    changed.value = true
    version.value++
  }

  /** One shape per target: linking a target that is already on another shape moves it. */
  function link(key: string, targetId: string): void {
    const e = element(key)
    const target = targetById.value.get(targetId)
    if (!e || !target) return

    svg.value!.querySelectorAll(`[id="_${target.id}"]`).forEach((other) => {
      if (other === e) return
      other.removeAttribute('id')
      history.value.push(`${target.label} saiu de ${nameOf(other)}.`)
    })

    const before = e.id || 'sem id'
    e.id = `_${target.id}`
    recordChange(`${nameOf(e)}: ${before} → ${target.label}`)
  }

  /** The designer's original id is gone after the rewrite, so unlinking removes the id. */
  function unlink(key: string): void {
    const e = element(key)
    if (!e?.id) return

    const before = e.id
    e.removeAttribute('id')
    recordChange(`${nameOf(e)}: ${before} removido.`)
  }

  /** Why these shapes can't be grouped, or null when they can. */
  function groupBlocker(keys: string[]): string | null {
    if (keys.length < 2) return 'Selecione 2 ou mais formas (Ctrl+clique)'

    const parents = new Set(keys.map(key => element(key)?.parentElement))
    if (parents.size > 1) return 'As formas precisam estar dentro do mesmo grupo'

    return null
  }

  /**
   * Wraps sibling shapes in a new `<g>` where the first of them was, so nothing moves on screen.
   * Returns the new group's key, or null when they can't be grouped.
   */
  function group(keys: string[]): string | null {
    if (groupBlocker(keys)) return null

    const parent = element(keys[0])!.parentElement!
    const elements = [ ...parent.children ].filter(child => keys.includes(keyOf(child) ?? ''))

    const g = document.createElementNS(SVG_NS, 'g')
    const key = `s${nextKey++}`
    g.setAttribute(KEY, key)
    parent.insertBefore(g, elements[0])
    elements.forEach(e => g.appendChild(e))

    recordChange(`Novo grupo ${key} com ${elements.map(nameOf).join(', ')}.`)

    return key
  }

  /** Why the selection can't be ungrouped, or null when it can. */
  function ungroupBlocker(keys: string[]): string | null {
    const e = keys.length === 1 ? element(keys[0]) : null
    if (e?.localName !== 'g') return 'Selecione um grupo'

    const own = [ ...e.attributes ].map(a => a.name).filter(name => name !== 'id' && !name.startsWith(OWN))
    if (own.length > 0) return `O grupo tem ${own.join(', ')}: desfazer mudaria o desenho`

    return null
  }

  /** Moves a group's children up to its parent and removes it, link included. Returns their keys. */
  function ungroup(key: string): string[] {
    if (ungroupBlocker([ key ])) return []

    const g = element(key)!
    const children = [ ...g.children ]
    children.forEach(child => g.parentElement!.insertBefore(child, g))
    g.remove()

    recordChange(`Grupo ${key}${g.id ? ` (${g.id})` : ''} desfeito: ${children.length} forma(s) voltaram ao grupo de cima.`)

    return children.flatMap(child => keyOf(child) ?? [])
  }

  /** The SVG to store as the Mapping's image, without the tool's own attributes. */
  function exportSvg(): string {
    const clone = svg.value!.cloneNode(true) as SVGSVGElement

    clone.querySelectorAll('*').forEach((e) => {
      for (const attribute of [ ...e.attributes ]) {
        if (attribute.name.startsWith(OWN)) e.removeAttribute(attribute.name)
      }
    })

    return new XMLSerializer().serializeToString(clone)
  }

  return {
    svg,
    version,
    targets,
    shapes,
    issues,
    errorCount,
    smallCount,
    history,
    changed,
    load,
    clear,
    measure,
    element,
    keyAt,
    link,
    unlink,
    groupBlocker,
    group,
    ungroupBlocker,
    ungroup,
    exportSvg,
  }
}

export type SectorMapLinking = ReturnType<typeof useSectorMapLinking>
